import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  LinearProgress,
  Grid,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
} from '@mui/material';
import {
  History as HistoryIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { pretsAPI } from '../../../services/api';
import { Pret, PretHistoriqueResponse } from '../../../types/pret';
import { extractErrorMessage } from '../../../utils/errorHandler';
import useAlert from '../../../hooks/useAlert';

const HR_COLOR = '#00897B';

interface PretHistoriqueDialogProps {
  open: boolean;
  onClose: () => void;
  pret: Pret | null;
  onSuccess?: () => void;
}

const PretHistoriqueDialog: React.FC<PretHistoriqueDialogProps> = ({
  open,
  onClose,
  pret,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [historiqueData, setHistoriqueData] = useState<PretHistoriqueResponse | null>(null);

  const { showError } = useAlert();

  const fetchHistorique = async () => {
    if (!pret) return;
    setLoading(true);
    try {
      const data = await pretsAPI.getHistorique(pret.id);
      setHistoriqueData(data);
    } catch (error: any) {
      showError(extractErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && pret) {
      fetchHistorique();
    }
  }, [open, pret]);

  const formatMoney = (amount: number) => {
    return new Intl.NumberFormat('fr-MA', {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount) + ' DH';
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (!pret) return null;

  const currentPret = historiqueData?.pret || pret;
  const paiements = historiqueData?.paiements || [];

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: HR_COLOR, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HistoryIcon />
            <Typography variant="h6">Historique du Prêt</Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress sx={{ color: HR_COLOR }} />
            </Box>
          ) : (
            <>
              {/* Loan Summary */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ mb: 2 }}>
                  <Chip
                    label={currentPret.ref_pret || `#${currentPret.id}`}
                    size="small"
                    sx={{ bgcolor: '#1976D2', color: 'white', fontWeight: 'bold', mr: 1 }}
                  />
                  <Typography variant="body1" component="span" fontWeight="500">
                    {currentPret.employee?.nom_complet || 'N/A'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }} component="span">
                    ({currentPret.employee?.matricule})
                  </Typography>
                </Box>

                {/* Loan Details */}
                <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="text.secondary">Montant Total:</Typography>
                      <Typography variant="body1" fontWeight="500">
                        {formatMoney(currentPret.montant_pret)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="text.secondary">Mensualité:</Typography>
                      <Typography variant="body1" fontWeight="500">
                        {formatMoney(currentPret.prelevement_mensuel)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="text.secondary">Durée:</Typography>
                      <Typography variant="body1" fontWeight="500">
                        {currentPret.duree_mois} mois
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="text.secondary">Début Prélèvement:</Typography>
                      <Typography variant="body1" fontWeight="500">
                        {formatDate(currentPret.date_debut_prelevement)}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Current Status */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Situation Actuelle
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Box sx={{ p: 2, bgcolor: '#e3f2fd', borderRadius: 1, textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary">Total Remboursé</Typography>
                      <Typography variant="h6" sx={{ color: '#66BB6A', fontWeight: 'bold' }}>
                        {formatMoney(currentPret.montant_rembourse)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box sx={{ p: 2, bgcolor: '#ffebee', borderRadius: 1, textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary">Reste à Payer</Typography>
                      <Typography variant="h6" sx={{ color: '#EF5350', fontWeight: 'bold' }}>
                        {formatMoney(currentPret.montant_restant)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box sx={{ p: 2, bgcolor: '#fff3e0', borderRadius: 1, textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary">Mois Restants</Typography>
                      <Typography variant="h6" sx={{ color: '#FF9800', fontWeight: 'bold' }}>
                        {currentPret.mois_restants ?? '-'} mois
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                {/* Progress Bar */}
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2">Progression</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {currentPret.pourcentage_rembourse.toFixed(1)}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(100, currentPret.pourcentage_rembourse)}
                    sx={{
                      height: 10,
                      borderRadius: 5,
                      bgcolor: '#e0e0e0',
                      '& .MuiLinearProgress-bar': {
                        bgcolor: currentPret.pourcentage_rembourse >= 100 ? '#66BB6A' : HR_COLOR,
                      },
                    }}
                  />
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Payment History */}
              <Box>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Historique des Paiements ({paiements.length})
                </Typography>
                {paiements.length === 0 ? (
                  <Box sx={{ p: 3, textAlign: 'center', bgcolor: '#fafafa', borderRadius: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Aucun paiement enregistré
                    </Typography>
                  </Box>
                ) : (
                  <TableContainer sx={{ maxHeight: 300 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>#</TableCell>
                          <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>Date</TableCell>
                          <TableCell align="right" sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>Montant</TableCell>
                          <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>Enregistré par</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paiements.map((paiement, index) => (
                          <TableRow key={paiement.id} hover>
                            <TableCell>{paiements.length - index}</TableCell>
                            <TableCell>{formatDateTime(paiement.date_paiement)}</TableCell>
                            <TableCell align="right" sx={{ color: '#66BB6A', fontWeight: 'bold' }}>
                              {formatMoney(paiement.montant)}
                            </TableCell>
                            <TableCell>
                              {paiement.creator?.name || paiement.creator?.email || '-'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose} variant="outlined">
            Fermer
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PretHistoriqueDialog;
